import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Calculator } from "lucide-react";
import { toast } from "sonner";
import BulkUploadButton from "@/components/BulkUploadButton";

const defaultInputs = {
    no_of_machines: 1, investment_lacs: 0, connected_load_kw: 5, load_factor: 0.9,
    power_rate: 7.5, oee: 0.9, hrs_per_day: 20, days_per_year: 275,
    no_of_workers: 1, avg_emp_cost_month: 20000, interest_rate_pct: 12,
    depreciation_rate: 0.2, consumables_rate_per_hr: 80, maintenance_lacs: 1.485,
    production_overhead_pct: 0.25,
};

export default function Machines() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState("");
    const [inputs, setInputs] = useState(defaultInputs);
    const [preview, setPreview] = useState(null);

    const load = async () => {
        const res = await api.get("/machines");
        setList(res.data);
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        const t = setTimeout(async () => {
            try {
                const numericInputs = Object.fromEntries(
                    Object.entries(inputs).map(([k, v]) => [k, Number(v) || 0])
                );
                const res = await api.post("/mhr/preview", numericInputs);
                setPreview(res.data);
            } catch (e) { console.warn("MHR preview failed:", e); }
        }, 250);
        return () => clearTimeout(t);
    }, [inputs]);

    const startCreate = () => {
        setEditing(null);
        setName("");
        setInputs(defaultInputs);
        setOpen(true);
    };
    const startEdit = (m) => {
        setEditing(m);
        setName(m.name);
        setInputs(m.inputs);
        setOpen(true);
    };

    const save = async () => {
        if (!name.trim()) { toast.error("Machine name required"); return; }
        const numericInputs = Object.fromEntries(
            Object.entries(inputs).map(([k, v]) => [k, Number(v) || 0])
        );
        try {
            if (editing) {
                await api.put(`/machines/${editing.machine_id}`, { name, inputs: numericInputs });
            } else {
                await api.post("/machines", { name, inputs: numericInputs });
            }
            setOpen(false);
            await load();
            toast.success(editing ? "Machine updated" : "Machine created");
        } catch {
            toast.error("Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this machine?")) return;
        await api.delete(`/machines/${id}`);
        await load();
    };

    return (
        <div>
            <PageHeader
                eyebrow="Master"
                title="Machines & MHR"
                subtitle="Add machines with their investment, power and operating parameters. We compute the Machine Hour Rate (Rs/hr) on the fly."
                actions={
                    <div className="flex gap-2">
                        <BulkUploadButton entity="machines" onComplete={load} />
                        <Button onClick={startCreate} className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" data-testid="new-machine-button">
                            <Plus size={16} className="mr-2" /> Add Machine
                        </Button>
                    </div>
                }
            />
            <div className="px-8 py-6">
                {list.length === 0 ? (
                    <EmptyMachines onCreate={startCreate} />
                ) : (
                    <table className="w-full sheet-table" data-testid="machines-table">
                        <thead>
                            <tr>
                                <th>Machine</th>
                                <th>Investment (₹ lacs)</th>
                                <th>Power (kW)</th>
                                <th>Hrs/year</th>
                                <th>Total Cost (₹ lacs)</th>
                                <th>Quotation MHR (₹/hr)<br/><span className="text-[9px] text-zinc-400 normal-case">auto-computed</span></th>
                                <th>Actual MHR (₹/hr)<br/><span className="text-[9px] text-zinc-400 normal-case">leave blank = same as Quotation</span></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((m) => (
                                <MachineRow key={m.machine_id} m={m} onUpdated={load} onEdit={startEdit} onRemove={remove} />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-none max-w-5xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Calculator size={18} /> {editing ? "Edit Machine" : "Add Machine"} — MHR Calculator
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-5 gap-6">
                        <div className="col-span-3 space-y-3 max-h-[60vh] overflow-auto pr-2">
                            <Field label="Machine Name *">
                                <Input data-testid="machine-name-input" value={name} onChange={(e) => setName(e.target.value)} />
                            </Field>
                            <SectionRule label="Investment & Power" />
                            <Grid2>
                                <NumberField label="No. of Machines" v={inputs.no_of_machines} k="no_of_machines" setInputs={setInputs} inputs={inputs} />
                                <NumberField label="Total Investment (₹ lacs)" v={inputs.investment_lacs} k="investment_lacs" setInputs={setInputs} inputs={inputs} testid="investment-input" />
                                <NumberField label="Connected Load (kW)" v={inputs.connected_load_kw} k="connected_load_kw" setInputs={setInputs} inputs={inputs} />
                                <NumberField label="Load Factor" v={inputs.load_factor} k="load_factor" setInputs={setInputs} inputs={inputs} step="0.01" />
                                <NumberField label="Power Rate (₹/unit)" v={inputs.power_rate} k="power_rate" setInputs={setInputs} inputs={inputs} step="0.1" />
                                <NumberField label="OEE" v={inputs.oee} k="oee" setInputs={setInputs} inputs={inputs} step="0.01" />
                            </Grid2>
                            <SectionRule label="Operating Hours" />
                            <Grid2>
                                <NumberField label="Hrs / Day" v={inputs.hrs_per_day} k="hrs_per_day" setInputs={setInputs} inputs={inputs} />
                                <NumberField label="Days / Year" v={inputs.days_per_year} k="days_per_year" setInputs={setInputs} inputs={inputs} />
                            </Grid2>
                            <SectionRule label="Manpower" />
                            <Grid2>
                                <NumberField label="No. of Workers" v={inputs.no_of_workers} k="no_of_workers" setInputs={setInputs} inputs={inputs} />
                                <NumberField label="Avg Emp Cost (₹/month)" v={inputs.avg_emp_cost_month} k="avg_emp_cost_month" setInputs={setInputs} inputs={inputs} />
                            </Grid2>
                            <SectionRule label="Cost Rates" />
                            <Grid2>
                                <NumberField label="Consumables (₹/hr)" v={inputs.consumables_rate_per_hr} k="consumables_rate_per_hr" setInputs={setInputs} inputs={inputs} />
                                <NumberField label="Maintenance (₹ lacs/yr)" v={inputs.maintenance_lacs} k="maintenance_lacs" setInputs={setInputs} inputs={inputs} step="0.01" />
                                <NumberField label="Interest Rate (%)" v={inputs.interest_rate_pct} k="interest_rate_pct" setInputs={setInputs} inputs={inputs} step="0.1" />
                                <NumberField label="Depreciation Rate" v={inputs.depreciation_rate} k="depreciation_rate" setInputs={setInputs} inputs={inputs} step="0.01" />
                                <NumberField label="Production Overhead %" v={inputs.production_overhead_pct} k="production_overhead_pct" setInputs={setInputs} inputs={inputs} step="0.01" />
                            </Grid2>
                        </div>

                        <div className="col-span-2 border-l border-zinc-200 pl-6">
                            <div className="tag-pill">Live Breakdown</div>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-4">Final MHR</div>
                            <div className="text-4xl font-bold font-mono text-[#002FA7] mt-1" data-testid="mhr-preview-value">
                                ₹ {preview?.mhr_rs_per_hr ?? "—"}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1 font-mono">per machine hour</div>
                            <table className="w-full sheet-table mt-4 text-[12px]">
                                <tbody>
                                    <Row label="Hrs/yr per machine" v={preview?.hrs_per_year_per_machine} />
                                    <Row label="Total Hrs/yr" v={preview?.total_hrs_per_year} />
                                    <Row label="Consumables" v={preview?.consumables_lacs} unit="L" />
                                    <Row label="Power" v={preview?.power_lacs} unit="L" />
                                    <Row label="Direct Labour" v={preview?.direct_labour_lacs} unit="L" />
                                    <Row label="Maintenance" v={preview?.maintenance_lacs} unit="L" />
                                    <Row label="Subtotal Direct" v={preview?.subtotal_direct_lacs} unit="L" strong />
                                    <Row label="Prod Overhead" v={preview?.production_overhead_lacs} unit="L" />
                                    <Row label="LT Interest" v={preview?.long_term_interest_lacs} unit="L" />
                                    <Row label="Depreciation" v={preview?.depreciation_lacs} unit="L" />
                                    <Row label="Subtotal Overhead" v={preview?.subtotal_overhead_lacs} unit="L" strong />
                                    <Row label="Total Cost" v={preview?.total_cost_lacs} unit="L" strong />
                                </tbody>
                            </table>
                            <p className="text-[10px] text-zinc-400 mt-3 font-mono">L = ₹ lacs (100,000)</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" onClick={save} data-testid="save-machine-button">
                            {editing ? "Update" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>
            {children}
        </div>
    );
}
function Grid2({ children }) { return <div className="grid grid-cols-2 gap-3">{children}</div>; }
function SectionRule({ label }) {
    return <div className="pt-2 pb-1 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</div>;
}
function NumberField({ label, v, k, setInputs, inputs, step = "1", testid }) {
    return (
        <Field label={label}>
            <Input
                type="number"
                step={step}
                value={v}
                onChange={(e) => setInputs({ ...inputs, [k]: e.target.value })}
                className="font-mono"
                data-testid={testid}
            />
        </Field>
    );
}
function Row({ label, v, unit, strong }) {
    return (
        <tr>
            <td className={strong ? "font-semibold" : ""}>{label}</td>
            <td className={`text-right font-mono ${strong ? "font-semibold" : ""}`}>{v ?? "—"}{unit ? " " + unit : ""}</td>
        </tr>
    );
}
function EmptyMachines({ onCreate }) {
    return (
        <div className="border-2 border-dashed border-zinc-300 p-16 text-center">
            <div className="tag-pill mb-4 inline-flex">Empty</div>
            <h2 className="text-2xl font-bold tracking-tight">No machines yet</h2>
            <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
                Add your first machine to compute its Machine Hour Rate. You'll reference this in processes.
            </p>
            <Button onClick={onCreate} className="mt-6 rounded-none bg-[#002FA7] hover:bg-[#00227B]">
                <Plus size={16} className="mr-2" /> Add Machine
            </Button>
        </div>
    );
}

function MachineRow({ m, onUpdated, onEdit, onRemove }) {
    // Local-state input so typing doesn't trigger a re-fetch + re-render on
    // every keystroke (which was causing the input to lose focus mid-type).
    // Commits on blur or Enter.
    const [val, setVal] = React.useState(m.mhr_rs_per_hr_zbc ?? "");
    React.useEffect(() => { setVal(m.mhr_rs_per_hr_zbc ?? ""); }, [m.mhr_rs_per_hr_zbc]);
    const commit = async () => {
        const cur = val === "" ? null : Number(val);
        if (cur === (m.mhr_rs_per_hr_zbc ?? null)) return;
        try {
            await api.put(`/machines/${m.machine_id}`, { name: m.name, inputs: m.inputs, mhr_rs_per_hr_zbc: cur });
            await onUpdated();
            toast.success("Actual MHR saved");
        } catch { toast.error("Save failed"); }
    };
    return (
        <tr data-testid={`machine-row-${m.machine_id}`}>
            <td className="font-medium">{m.name}</td>
            <td className="font-mono">{m.inputs.investment_lacs}</td>
            <td className="font-mono">{m.inputs.connected_load_kw}</td>
            <td className="font-mono">{m.breakdown.total_hrs_per_year}</td>
            <td className="font-mono">{m.breakdown.total_cost_lacs}</td>
            <td className="font-mono font-semibold text-[#002FA7]" data-testid={`mhr-${m.machine_id}`}>₹ {m.breakdown.mhr_rs_per_hr}</td>
            <td>
                <Input
                    type="number"
                    step="0.01"
                    value={val}
                    placeholder={`= ${m.breakdown.mhr_rs_per_hr}`}
                    onChange={(e) => setVal(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    className="h-7 rounded-none font-mono w-24"
                    data-testid={`machine-zbc-${m.machine_id}`}
                />
            </td>
            <td className="text-right w-32">
                <button onClick={() => onEdit(m)} className="text-[#002FA7] hover:underline text-xs uppercase tracking-wider">Edit</button>
                <button onClick={() => onRemove(m.machine_id)} className="ml-3 text-zinc-400 hover:text-[#E63946]">
                    <Trash2 size={14} />
                </button>
            </td>
        </tr>
    );
}

